// Extra Work — a daily tracker. One card per issue; open it full-sized and Add Day.
// Each day records the crew, the material and the equipment used (picked from the real
// lists, quantities only) plus its own notes and photos. The MONEY is office-only: the
// office/PM see the derived price and the by-day summary; the field just logs quantities.
// Built to read well on a phone — the field logs from theirs.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, API_BASE, getToken, photoUrl } from '../../services/apiClient';
import SearchSelect from '../../components/SearchSelect';
import AttachButton, { AttachThumb } from '../../components/AttachButton';
import { tmUnitOf } from './TmTile';
import { partLabel } from '../../utils/billParts';
import { FieldwireTasks, FieldwireTaskDrawer, FwTaskChooser } from './FieldwirePanel';

const money = n => (n == null || n === '') ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = v => (v === '' || v == null) ? null : Number(v);
const shortDate = s => s ? new Date(String(s).length <= 10 ? s + 'T12:00:00' : s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
const ymd = d => { const x = new Date(d); x.setHours(12, 0, 0, 0); return x.toISOString().slice(0, 10); };
// An extra-work card carries a number from the day it's made. Until it's promoted to a
// Change Order it's just Extra Work, so it reads EW001, EW002…; once promoted it becomes
// its CO number. e.g. "2624 EW002", then "2624 CO-2".
const ewCode = t => t && t.co_number ? (t.promoted_at ? `CO-${t.co_number}` : `EW${String(t.co_number).padStart(3, '0')}`) : '';

function useNarrow() {
  const [n, setN] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => { const f = () => setN(window.innerWidth < 640); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return n;
}

// Quoted comes first: on a T&M job the lines the customer was quoted (a crew day rate,
// a drill hour, a mobilization) are what most days are made of, so they're the first
// thing offered. The tab only shows when the job has quote items.
const CATS = [
  { key: 'quoted',    label: 'Quoted',    icon: '📋', unit: '',    src: 'tm_item' },
  { key: 'labor',     label: 'Labor',     icon: '👷', unit: 'hr',  src: 'crew' },
  { key: 'material',  label: 'Material',  icon: '🧱', unit: 'ea',  src: 'part' },
  { key: 'equipment', label: 'Equipment', icon: '🚜', unit: 'day', src: 'equipment' },
  // A purchase order written on this job — material bought for the work, billed at the PO's cost.
  { key: 'po',        label: 'PO',        icon: '🧾', unit: 'ea',  src: 'po' },
  { key: 'other',     label: 'Other',     icon: '📝', unit: '',    src: 'free' },
];
const CAT = Object.fromEntries(CATS.map(c => [c.key, c]));
const cell = { fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, padding: '9px 10px', fontFamily: 'inherit', boxSizing: 'border-box' };

// `single` = one implicit container tracked directly (the whole job is T&M) — no card
// list. Otherwise it's the Extra Work model: a card per issue. `noun` labels it.
export default function ExtraWorkDaily({ sjId, projectId, projectNumber = '', canEdit = false, canSeeMoney = false, single = false, noun = 'Extra Work', workflow = null, show = 'all', rateSource = null }) {
  const [cards, setCards] = useState(null);
  const [open, setOpen] = useState(null);
  const [making, setMaking] = useState(false);

  const load = useCallback(() => {
    apiFetch(`/sub-jobs/${sjId}/extra-work`).then(t => setCards(Array.isArray(t) ? t.filter(x => x.type !== 'unit_price') : [])).catch(() => setCards([]));
  }, [sjId]);
  useEffect(() => { load(); }, [load]);

  // Single mode: make sure the one container exists, then track it directly.
  useEffect(() => {
    if (!single || cards === null || cards.length > 0 || !canEdit || making) return;
    setMaking(true);
    onceFor('ticket:' + sjId, async () => {
      // Look again first: another tracker on the same card may have just made it.
      const have = await apiFetch(`/sub-jobs/${sjId}/extra-work`).catch(() => []);
      const tm = (Array.isArray(have) ? have : []).find(x => x.type !== 'unit_price');
      return tm || apiFetch(`/sub-jobs/${sjId}/extra-work`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: noun, type: 'tm' }) });
    }).then(t => setCards([t])).catch(() => {}).finally(() => setMaking(false));
  }, [single, cards, canEdit, sjId, noun, making]);

  async function addCard() {
    const title = window.prompt('Name this extra work (what happened / why):', '');
    if (title == null) return;
    try {
      const t = await apiFetch(`/sub-jobs/${sjId}/extra-work`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: title.trim() || 'Extra Work', type: 'tm' }) });
      setCards(c => [...(c || []), t]); setOpen(t);
    } catch { /* ignore */ }
  }
  // Rename a card from the list — the PM and Executive asked for this on 2624 (Sep 2026):
  // a ticket named in a hurry in the field had no way to be fixed afterwards.
  async function renameCard(t) {
    const title = window.prompt('Rename this extra work:', t.title || '');
    if (title == null || title.trim() === (t.title || '')) return;
    try {
      const upd = await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: title.trim() || 'Extra Work' }) });
      setCards(c => (c || []).map(x => x.id === t.id ? { ...x, title: upd.title ?? title.trim() } : x));
    } catch (e) { window.alert(e?.error || e?.message || 'Could not rename it.'); }
  }
  // Delete a card and its days — the office, PMs and Executives (Ryan, Oct 29 2026: a test
  // job's extra work could not be removed, so the job could not be deleted either).
  async function deleteCard(t) {
    const name = [projectNumber, ewCode(t)].filter(Boolean).join(' ') || 'this extra work';
    if (!window.confirm(`Delete ${name} — "${t.title || 'Extra Work'}"?\n\nEvery day logged on it (labor, material, equipment, photos) goes with it. This cannot be undone.`)) return;
    try {
      await apiFetch(`/sub-jobs/${sjId}/extra-work/${t.id}`, { method: 'DELETE' });
      setCards(c => (c || []).filter(x => x.id !== t.id));
    } catch (e) { window.alert(e?.error || e?.message || 'Could not delete it.'); }
  }

  if (cards === null) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;
  if (single) {
    const card = cards[0];
    if (!card) return <div style={{ fontSize: 13, color: '#9ca3af' }}>{canEdit ? 'Setting up…' : `No ${noun} tracked yet.`}</div>;
    return <CardTracker ticket={card} sjId={sjId} projectId={projectId} projectNumber={projectNumber} canEdit={canEdit} canSeeMoney={canSeeMoney} single noun={noun} workflow={workflow} show={show} rateSource={rateSource} onBack={null} />;
  }
  // Which Fieldwire task each card is tied to — so the chooser greys those out, and the pins list
  // below leaves them off (Ryan, Minden Pool 2622, Oct 23 2026).
  const taken = Object.fromEntries(cards.filter(t => t.fw_task_id).map(t => [t.fw_task_id, [projectNumber, ewCode(t)].filter(Boolean).join(' ')]));
  if (open) return <CardTracker ticket={open} sjId={sjId} projectId={projectId} projectNumber={projectNumber} canEdit={canEdit} canSeeMoney={canSeeMoney} taken={taken} onBack={() => { setOpen(null); load(); }} />;

  return (
    <div>
      {/* The crew's pins in the Extra Work category that no card has yet — open one for its photos
          and comments; tie it to a card from inside the card (📍 Tie to a Fieldwire task). */}
      <div style={{ marginBottom: 12 }}>
        <FieldwireTasks projectId={projectId} subJobId={sjId} canEdit={canEdit} compact title="Extra Work pins in Fieldwire — not on a card yet" excludeIds={Object.keys(taken)} />
      </div>
      {cards.length === 0 ? (
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>No extra work tracked yet. Start a card for each separate issue.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {cards.map(t => (
            <button key={t.id} onClick={() => setOpen(t)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderRadius: 12, padding: '13px 14px', cursor: 'pointer' }}>
              <span style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: '#fef3c7', color: '#b45309', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🛠️</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#b45309' }}>{[projectNumber, ewCode(t)].filter(Boolean).join(' ')}</span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: '#111' }}>{t.title || 'Extra Work'}</span>
                <span style={{ display: 'block', fontSize: 12, color: '#8a8a8a' }}>{[t.promoted_at ? '✓ Change Order' : 'Open to track days', t.fw_task_id ? `📍 Fieldwire #${t.fw_task_number || ''}` : null].filter(Boolean).join(' · ')}</span>
              </span>
              {canEdit && (
                <span role="button" title="Rename" onClick={e => { e.stopPropagation(); renameCard(t); }}
                  style={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 9px', background: '#fff' }}>✎</span>
              )}
              {canEdit && canSeeMoney && !t.promoted_at && !t.locked_at && (
                <span role="button" title="Delete this extra work and its days" onClick={e => { e.stopPropagation(); deleteCard(t); }}
                  style={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 9px', background: '#fff' }}>🗑</span>
              )}
              <span style={{ flexShrink: 0, color: '#c4c4c4', fontSize: 20 }}>›</span>
            </button>
          ))}
        </div>
      )}
      {canEdit && (
        <button onClick={addCard} style={{ padding: '11px 16px', borderRadius: 10, border: '1.5px dashed #fbbf24', background: '#fffbeb', color: '#92400e', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
          + New Extra Work card
        </button>
      )}
    </div>
  );
}

// A T&M billing tracker keyed by PROJECT (not a sub-job): finds the one 'tm' container
// on the job — creating it the first time the office opens it — then hands off to the
// single-mode daily tracker. Used by the T&M Billing tile (desktop) and the small-job
// work-order card (mobile) so both show the exact same by-the-day T&M, not two views.
// Two trackers can sit on one card now (the loop card shows Common Items and Standard
// T&M as separate pills over the SAME days). Whatever they have to set up the first
// time — the job's T&M container — is made once, not once each.
const setupOnce = new Map();
function onceFor(key, make) {
  if (!setupOnce.has(key)) {
    const p = Promise.resolve().then(make);
    setupOnce.set(key, p);
    p.catch(() => {}).then(() => setTimeout(() => setupOnce.delete(key), 4000));
  }
  return setupOnce.get(key);
}

// show: 'all' (default — Common Items on top of each day, Standard T&M below),
//       'common' (only the job's quoted items and their quantities),
//       'standard' (only labor / material / equipment / PO / other, notes and photos).
export function TmProjectTracker({ projectId, projectNumber = '', canEdit = false, canSeeMoney = false, noun = 'T&M', workflow = null, show = 'all', rateSource = null }) {
  const [sjId, setSjId] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const tm = await onceFor('tm:' + projectId + ':' + (canEdit ? 1 : 0), async () => {
          const subs = await apiFetch(`/projects/${projectId}/sub-jobs`);
          const found = (Array.isArray(subs) ? subs : []).find(s => s.type === 'tm');
          if (found || !canEdit) return found || null;
          return apiFetch(`/projects/${projectId}/sub-jobs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'tm', name: 'T&M' }) });
        });
        if (live) setSjId(tm ? tm.id : null);
      } catch { if (live) setErr(true); }
    })();
    return () => { live = false; };
  }, [projectId, canEdit]);
  if (err) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Couldn’t load T&M billing — try again.</div>;
  if (!sjId) return <div style={{ fontSize: 13, color: '#9ca3af' }}>{canEdit ? 'Setting up…' : 'No T&M billed yet.'}</div>;
  return <ExtraWorkDaily sjId={sjId} projectId={projectId} projectNumber={projectNumber} canEdit={canEdit} canSeeMoney={canSeeMoney} single noun={noun} workflow={workflow} show={show} rateSource={rateSource} />;
}

// ── The full-sized daily tracker for one card ───────────────────────────────────
// One ticket's day-by-day log, for embedding inside another card — the Change Order card
// uses it in place of its old line table. Same days, same Common Items / Standard T&M
// sections, same rules about who sees money.
export function TicketDayLog({ ticket, sjId, projectId, projectNumber = '', canEdit = false, canSeeMoney = false, workflow = null, onData = null }) {
  return <CardTracker ticket={ticket} sjId={sjId} projectId={projectId} projectNumber={projectNumber} canEdit={canEdit} canSeeMoney={canSeeMoney}
    single noun="Tracked by the day" workflow={workflow} onBack={null} onData={onData} />;
}

// Words for which group leads on the card (projects.tm_rate_source).
export const TM_SOURCE_LABEL = { rate_sheet: 'Job Rate Sheet', standard: 'Standard T&M', loops: 'Loops', common: 'Common Items', pole_hold: 'Pole Hold', drilling: 'Drilling' };
export const TM_SOURCES = Object.entries(TM_SOURCE_LABEL);

// A closed billing part on the card (Pat, Oct 9 2026 — WO26117): its days stay here to read, folded
// under one line, but nothing can be added or changed. The office can rename it, or reopen the last one.
function ClosedPart({ name, info, days, renderDay, canManage, isLast, onRename, onReopen, canSeeMoney, total }) {
  const [open, setOpen] = useState(false);
  const md = d => (d ? shortDate(d) : '');
  const span = days.length ? `${md(days[days.length - 1].work_date)}${days.length > 1 ? ' – ' + md(days[0].work_date) : ''}` : '';
  const btn = { padding: '4px 9px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#4b5563', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: 12, background: '#f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px' }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px', minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <span style={{ fontSize: 16 }}>🔒</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: '#111' }}>{partLabel(name)} · billed</span>
            <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>
              {[info && info.invoice_no ? `Invoice ${info.invoice_no}${info.invoice_status ? ' (' + info.invoice_status + ')' : ''}` : info && info.bill_ref ? info.bill_ref : null,
                canSeeMoney ? money(total) : null, span, `${days.length} day${days.length === 1 ? '' : 's'}`, 'read only'].filter(Boolean).join(' · ')}
            </span>
          </span>
          <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{open ? 'Hide days ▴' : 'Show days ▾'}</span>
        </button>
        {canManage && info && <button onClick={() => onRename(info)} style={btn} title="Rename this part">✎ Rename</button>}
        {canManage && info && isLast && <button onClick={() => onReopen(info)} style={btn} title="Closed by mistake? Its days can be changed again">↺ Reopen</button>}
      </div>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 10px 12px' }}>{days.map(renderDay)}</div>}
    </div>
  );
}

function CardTracker({ ticket: ticketIn, sjId, projectId, projectNumber = '', canEdit, canSeeMoney, onBack, single = false, noun = 'Extra Work', workflow = null, onData = null, show = 'all', rateSource = null, taken = {} }) {
  const narrow = useNarrow();
  // The ticket as it changes here — tied to a Fieldwire task, made a change order.
  const [ticket, setTicket] = useState(ticketIn);
  useEffect(() => { setTicket(ticketIn); }, [ticketIn]);
  const [fwOv, setFwOv] = useState(null);         // the job's Fieldwire link (statuses, open url)
  const [fwOpen, setFwOpen] = useState(false);    // the task's photos + comments drawer
  const [tying, setTying] = useState(false);      // the "which task?" chooser
  const [fwBusy, setFwBusy] = useState(false);
  const [coBusy, setCoBusy] = useState(false);
  useEffect(() => { if (!single && projectId) apiFetch(`/projects/${projectId}/fieldwire`).then(setFwOv).catch(() => setFwOv(null)); }, [single, projectId]);
  const [catIds, setCatIds] = useState([]);       // pins in this card's own Fieldwire category — first in the chooser
  useEffect(() => { if (tying) apiFetch(`/projects/${projectId}/fieldwire/tasks?subJob=${sjId}`).then(r => setCatIds(Array.isArray(r) ? r.map(x => x.id) : [])).catch(() => {}); }, [tying, projectId, sjId]);
  const tieTo = async taskId => {
    setFwBusy(true);
    try {
      const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/fieldwire/link`, { method: 'POST', body: JSON.stringify({ task_id: taskId }) });
      setTicket(t => ({ ...t, fw_task_id: r.fw_task_id, fw_task_number: r.fw_task_number })); setTying(false); setFwOpen(true);
    } catch (e) { window.alert(e?.error || e?.message || 'Could not tie it.'); }
    setFwBusy(false);
  };
  const untie = async () => {
    if (!window.confirm(`Untie Fieldwire task #${ticket.fw_task_number || ''} from this card?\n\nThe task stays in Fieldwire.`)) return;
    try { await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/fieldwire/link`, { method: 'DELETE' }); setTicket(t => ({ ...t, fw_task_id: null, fw_task_number: null, fw_task_url: null })); }
    catch (e) { window.alert(e?.error || e?.message || 'Could not untie it.'); }
  };
  // "→ Make it a Change Order" — the same promote the old job page and the RFI card use: the SAME
  // record moves onto the Change Orders tile, priced (it was missing on this card — Ryan, Oct 23 2026).
  const makeCo = async () => {
    if (!window.confirm('Make this extra work a Change Order?\n\nIt stays the same record — the crew keep seeing it here with no prices, and it shows priced on the Change Orders tile. Anything they add still comes through, until the billing summary is printed.')) return;
    setCoBusy(true);
    try { const r = await apiFetch(`/sub-jobs/${sjId}/extra-work/${ticket.id}/promote`, { method: 'POST' }); setTicket(t => ({ ...t, promoted_at: r.promoted_at || new Date().toISOString() })); }
    catch (e) { window.alert(e?.error || e?.message || 'Could not make it a change order.'); }
    setCoBusy(false);
  };
  // Billing parts (WO26117): the office, PMs and Executives see and manage them; anyone sees a
  // closed part's days folded and read-only.
  const [bill, setBill] = useState(null);
  const [closing, setClosing] = useState(null);   // { name, through, invoice_id } while the close form is open
  const loadBill = useCallback(() => {
    if (!single || !canSeeMoney || !projectId) return;
    apiFetch(`/projects/${projectId}/bill-parts`).then(setBill).catch(() => setBill(null));
  }, [single, canSeeMoney, projectId]);
  useEffect(() => { loadBill(); }, [loadBill]);
  // Which group leads on each day: the contract's Rate Sheet, this job's Common Items,
  // or Standard T&M (Pat, 2633, Sep 2026). Set on the job's T&M Rates tile; the tile on
  // the same page tells us when it changes.
  const [src, setSrc] = useState(rateSource || null);
  useEffect(() => { setSrc(rateSource || null); }, [rateSource]);
  useEffect(() => {
    const on = e => { if (e.detail && e.detail.projectId === projectId) setSrc(e.detail.source || null); };
    window.addEventListener('titan:tm-rate-source-changed', on);
    return () => window.removeEventListener('titan:tm-rate-source-changed', on);
  }, [projectId]);
  const onlyCommon = show === 'common', onlyStandard = show === 'standard';
  const inView = l => (onlyCommon ? l.category === 'quoted' : onlyStandard ? l.category !== 'quoted' : true);
  const viewTotal = d => (show === 'all' ? d.day_total : (d.lines || []).filter(inView).reduce((s, l) => s + (Number(l.price) || 0), 0));
  const me = useRef(Math.random().toString(36).slice(2));
  const [data, setData] = useState(null);
  const [crew, setCrew] = useState([]);
  const [parts, setParts] = useState([]);
  const [equip, setEquip] = useState([]);
  const [quoted, setQuoted] = useState([]);       // the job's T&M quote items — offered first
  const [pos, setPos] = useState([]);             // purchase orders written on this job — the PO tab
  const [pastDate, setPastDate] = useState('');   // office: add a day for a specific past date
  const [addingCommonTop, setAddingCommonTop] = useState(false);   // picking a common item before any day exists
  const base = `/sub-jobs/${sjId}/extra-work/${ticket.id}`;

  const load = useCallback(() => { apiFetch(`${base}/days`).then(setData).catch(() => setData({ days: [], total: 0 })); loadBill(); }, [base, loadBill]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const on = e => { if (!e.detail || !e.detail.projectId || e.detail.projectId === projectId) load(); };
    window.addEventListener('titan:bill-parts-changed', on);
    return () => window.removeEventListener('titan:bill-parts-changed', on);
  }, [load, projectId]);
  const billChanged = () => { load(); window.dispatchEvent(new CustomEvent('titan:bill-parts-changed', { detail: { projectId } })); };
  async function closePart() {
    try {
      await apiFetch(`/projects/${projectId}/bill-parts/close`, { method: 'POST', body: JSON.stringify({ name: (closing.name || '').trim(), through: closing.through || null, invoice_id: closing.invoice_id || null }) });
      setClosing(null); billChanged();
    } catch (e) { window.alert(e?.error || 'That part did not close.'); }
  }
  async function renamePart(p) {
    const nm = window.prompt('Rename this part (A, B, Phase 1, Trip 2…):', p.name);
    if (nm == null || !nm.trim() || nm.trim() === p.name) return;
    try { await apiFetch(`/projects/${projectId}/bill-parts/${p.id}`, { method: 'PATCH', body: JSON.stringify({ name: nm.trim() }) }); billChanged(); }
    catch (e) { window.alert(e?.error || 'Could not rename it.'); }
  }
  async function reopenPart(p) {
    if (!window.confirm(`Reopen ${partLabel(p.name)}? Its days can be changed again, and the next bill will include them.`)) return;
    try { await apiFetch(`/projects/${projectId}/bill-parts/${p.id}/reopen`, { method: 'POST' }); billChanged(); }
    catch (e) { window.alert(e?.error || 'Could not reopen it.'); }
  }
  const changed = useCallback(() => { load(); window.dispatchEvent(new CustomEvent('titan:ew-days-changed', { detail: { base, from: me.current } })); }, [load, base]);
  useEffect(() => {
    const other = e => { if (e.detail && e.detail.base === base && e.detail.from !== me.current) load(); };
    window.addEventListener('titan:ew-days-changed', other);
    return () => window.removeEventListener('titan:ew-days-changed', other);
  }, [base, load]);
  // Whoever embeds the tracker (the Change Order card) prices from these days.
  useEffect(() => { if (onData && data) onData(data); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    apiFetch('/users').then(u => setCrew(Array.isArray(u) ? u : [])).catch(() => {});
    apiFetch('/parts').then(p => setParts(Array.isArray(p) ? p : [])).catch(() => {});
    apiFetch('/equipment').then(e => setEquip(Array.isArray(e) ? e : [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!projectId) return;
    const loadQuoted = () => apiFetch(`/projects/${projectId}/tm-items`).then(r => setQuoted(Array.isArray(r) ? r : [])).catch(() => {});
    loadQuoted();
    apiFetch(`/projects/${projectId}/purchase-orders`).then(p => setPos(Array.isArray(p) ? p : [])).catch(() => {});
    window.addEventListener('titan:tm-items-changed', loadQuoted);
    return () => window.removeEventListener('titan:tm-items-changed', loadQuoted);
  }, [projectId]);

  // Quoted lines carry their unit (day / hr / trip / the unit label). The rate shows
  // to the office only — the field sees the name and the unit.
  const quotedOpts = useMemo(() => quoted.map(it => {
    const unit = tmUnitOf(it);
    return { value: it.id, label: it.name, unit, type: it.type, inherited: !!it.inherited, from: it.inherited_from || null, sheet: !!it.inherited || it.source_type === 'rate_sheet',
      sub: canSeeMoney && it.rate != null ? `${money(it.rate)} / ${unit}` : `per ${unit}` };
  }), [quoted, canSeeMoney]);
  // The contract's lines (linked) apart from this job's own.
  const sheetOpts = useMemo(() => quotedOpts.filter(o => o.sheet), [quotedOpts]);
  const ownOpts = useMemo(() => quotedOpts.filter(o => !o.sheet), [quotedOpts]);
  const sheetFrom = sheetOpts.find(o => o.inherited)?.from || null;   // null = the company Rate Sheet, on this job
  // What the job's own group is called: the structure it was set up with, else Common Items.
  const ownLabel = ['loops', 'common', 'pole_hold', 'drilling'].includes(src) ? TM_SOURCE_LABEL[src] : 'Common Items';
  const crewOpts = useMemo(() => crew.map(u => ({ value: u.id, label: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim(), sub: u.role })), [crew]);
  const partOpts = useMemo(() => parts.map(p => ({ value: p.id, label: p.description, sub: [p.category, p.unit].filter(Boolean).join(' · '), unit: p.unit })), [parts]);
  const equipOpts = useMemo(() => equip.map(e => ({ value: e.id, label: e.asset_number ? `${e.asset_number} · ${e.name}` : e.name, sub: e.category })), [equip]);
  // This job's POs, most recent first — number and vendor to pick by, what it was and
  // (office only) what it cost underneath. Picking one bills it at the PO's cost.
  const poOpts = useMemo(() => pos.slice().sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))).slice(0, 80).map(po => {
    const when = po.created_at ? new Date(po.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const cost = canSeeMoney && po.cost != null && po.cost !== '' ? money(po.cost) : null;
    return { value: po.id, label: `#${po.po_number} · ${po.vendor || 'no vendor'}`, sub: [po.description, cost, when].filter(Boolean).join(' · '), unit: 'ea' };
  }), [pos, canSeeMoney]);

  // No argument → the next day after the last one (what the field taps as it works).
  // A date string → that exact day, so the office can add one in the past when the
  // field forgot it. A date that's already on the card is a no-op (no duplicate block).
  // The latest day that actually has items on it — what "same as last" copies from.
  const lastFilled = [...(data?.days || [])].filter(d => (d.lines || []).some(l => l.category !== 'po'))
    .sort((a, b) => String(b.work_date).localeCompare(String(a.work_date)))[0] || null;
  async function addDay(explicit, copyFrom) {
    let work_date = explicit;
    if (!work_date) {
      const last = (data?.days || []).map(d => d.work_date).filter(Boolean).sort().pop();
      work_date = last ? ymd(new Date(new Date(last + 'T12:00:00').getTime() + 86400000)) : ymd(new Date());
    } else if ((data?.days || []).some(d => !d.part_closed && String(d.work_date).slice(0, 10) === explicit)) {
      setPastDate(''); return;
    }
    try { await apiFetch(`${base}/days`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ work_date, copy_from: copyFrom || undefined }) }); setPastDate(''); changed(); }
    catch (e) { window.alert(e?.error || e?.message || 'That day did not save — check your connection and try again.'); }
  }

  // Newest day at the top, oldest at the bottom — the field logs the latest day and
  // wants to see it first without scrolling past a season of older ones.
  const orderedDays = data ? [...data.days].sort((a, b) => String(b.work_date).localeCompare(String(a.work_date))) : [];
  // The open part's days are the card as it always was; each closed part folds under one line.
  const openDays = orderedDays.filter(d => !d.part_closed);
  const closedGroups = [];
  for (const d of orderedDays.filter(x => x.part_closed)) {
    let g = closedGroups.find(x => x.id === d.part_id);
    if (!g) { g = { id: d.part_id, name: d.part_name, days: [] }; closedGroups.push(g); }
    g.days.push(d);
  }
  const partInfo = id => (bill && bill.parts ? bill.parts.find(p => p.id === id) : null);
  closedGroups.sort((a, b) => ((partInfo(b.id)?.seq || 0) - (partInfo(a.id)?.seq || 0)));
  const lastClosedId = bill && bill.parts ? [...bill.parts].filter(p => p.status === 'closed').sort((a, b) => b.seq - a.seq)[0]?.id : null;
  const openTotal = openDays.reduce((s, d) => s + (viewTotal(d) || 0), 0);
  const openLabel = bill && bill.open ? bill.open.label : (closedGroups.length ? 'Open part' : null);
  const canManageParts = !!(bill && bill.enabled);

  const nav = { background: 'var(--surface,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 700, color: 'var(--text,#374151)', cursor: 'pointer' };

  // Rename from inside the card. The title lives on the ticket the parent handed us, so
  // keep a local copy that the rename updates; the list re-reads on Back.
  const [title, setTitle] = useState(ticket.title || '');
  useEffect(() => { setTitle(ticket.title || ''); }, [ticket.id, ticket.title]);
  async function rename() {
    const next = window.prompt(`Rename this ${noun.toLowerCase()}:`, title);
    if (next == null || next.trim() === title) return;
    try {
      const upd = await apiFetch(base, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: next.trim() || noun }) });
      setTitle(upd.title ?? next.trim());
    } catch (e) { window.alert(e?.error || e?.message || 'Could not rename it.'); }
  }

  return (
    <div>
      {/* Header — stacks on a phone so nothing crams. */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: single ? 0 : 8 }}>
          {onBack && <button onClick={onBack} style={nav}>‹ {noun}</button>}
          {single && <div style={{ fontSize: 11, fontWeight: 800, color: onlyCommon ? '#6d28d9' : '#b45309', textTransform: 'uppercase', letterSpacing: 0.5 }}>{onlyCommon ? '📋 Common Items' : onlyStandard ? '⏱️ Standard T&M' : `⏱️ ${noun}`}{show === 'all' && src && TM_SOURCE_LABEL[src] ? <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#fff', background: src === 'rate_sheet' ? '#0f766e' : src === 'common' ? '#6d28d9' : '#1e3a5f', borderRadius: 999, padding: '2px 8px', letterSpacing: 0 }}>{TM_SOURCE_LABEL[src]} first</span> : null}</div>}
          {canSeeMoney && (
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              {closedGroups.length > 0 && <div style={{ fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{openLabel} · open</div>}
              <div style={{ fontSize: narrow ? 18 : 22, fontWeight: 800, color: '#111', lineHeight: 1.1 }}>{money(closedGroups.length ? openTotal : show === 'all' ? data?.total : (data?.days || []).reduce((s, d) => s + viewTotal(d), 0))}</div>
              {closedGroups.length > 0 && <div style={{ fontSize: 10.5, color: '#6b7280' }}>whole job {money(show === 'all' ? data?.total : (data?.days || []).reduce((s, d) => s + viewTotal(d), 0))}</div>}
              <div style={{ fontSize: 10.5, color: '#9ca3af' }}>{onlyCommon ? 'tracked on these items' : <>tracked{data?.rate_sheet ? ' · priced by the Rate Sheet (trade + machine class)' : data?.labor_rate ? ` · labor $${data.labor_rate}/hr` : ''}</>}</div>
            </div>
          )}
        </div>
        {!single && <>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.5 }}>🛠️ {[projectNumber, ewCode(ticket)].filter(Boolean).join(' ') || 'Extra Work'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: narrow ? 18 : 20, fontWeight: 800, color: '#111', lineHeight: 1.2 }}>{title || 'Extra Work'}</div>
            {canEdit && (
              <button onClick={rename} title="Rename this extra work"
                style={{ ...nav, padding: '5px 10px', fontSize: 12, color: '#6b7280' }}>✎ Rename</button>
            )}
          </div>
          {/* Fieldwire + Change Order (Ryan, Minden Pool 2622, Oct 23 2026). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {ticket.fw_task_id ? (<>
              <button onClick={() => setFwOpen(true)} title="The crew's photos and comments on this task"
                style={{ ...nav, padding: '7px 12px', fontSize: 13, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                📍 Fieldwire task #{ticket.fw_task_number || ''} · photos &amp; comments
              </button>
              {canEdit && <button onClick={untie} title="Take the tie off — the task stays in Fieldwire" style={{ ...nav, padding: '6px 10px', fontSize: 12, color: '#9ca3af' }}>Untie</button>}
            </>) : canEdit && fwOv && fwOv.linked && (
              <button onClick={() => setTying(true)} title="Tie this card to the pin the crew made in Fieldwire — its photos and comments show here"
                style={{ ...nav, padding: '7px 12px', fontSize: 13, color: '#1d4ed8', border: '1px dashed #93c5fd' }}>📍 Tie to a Fieldwire task</button>
            )}
            {ticket.promoted_at
              ? <span style={{ fontSize: 12.5, fontWeight: 800, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '6px 12px' }}>✓ Change Order — on the Change Orders tile</span>
              : canSeeMoney && !ticket.work_order_id && (
                <button onClick={makeCo} disabled={coBusy}
                  style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0f4c35', color: '#fff', fontSize: 13, fontWeight: 800, cursor: coBusy ? 'default' : 'pointer', opacity: coBusy ? 0.6 : 1 }}>
                  {coBusy ? 'Working…' : '→ Make it a Change Order'}
                </button>
              )}
          </div>
          {tying && (
            <FwTaskChooser projectId={projectId} attachOnly heading={`Tie ${ewCode(ticket) || 'this card'} to a Fieldwire task`}
              sub="Pick the pin the crew made — its photos and comments will show on this card." taken={taken} firstIds={catIds} busy={fwBusy}
              onAttach={tieTo} onCreate={() => {}} onClose={() => setTying(false)} />
          )}
          {fwOpen && ticket.fw_task_id && (
            <FieldwireTaskDrawer projectId={projectId} taskId={ticket.fw_task_id} statuses={(fwOv && fwOv.statuses) || []} canEdit={canEdit} onClose={() => setFwOpen(false)} />
          )}
        </>}
      </div>

      {/* What's on the job, up front — the contract's Rate Sheet lines (linked) and this
          job's own Common Items, the leading group first. They used to show only INSIDE a
          day, so a rate put on the job's T&M Rates was invisible on the card until someone
          happened to add a day (Pat, WO26126, Sep 2026). The office sees the rate; the
          field sees the unit. */}
      {data && !onlyStandard && (src === 'rate_sheet'
        ? [['rate_sheet', sheetOpts], ['common', ownOpts]] : [['common', ownOpts], ['rate_sheet', sheetOpts]]).map(([key, opts]) => opts.length > 0 && (
        <div key={key} style={{ border: `1px solid ${key === 'rate_sheet' ? '#99f6e4' : '#ddd6fe'}`, background: key === 'rate_sheet' ? '#f0fdfa' : '#faf5ff', borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: key === 'rate_sheet' ? '#0f766e' : '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
            {key === 'rate_sheet' ? 'Job Rate Sheet' : `${ownLabel} on this job`} <span style={{ fontWeight: 600, color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>· {key === 'rate_sheet' ? (sheetFrom ? `from ${sheetFrom} · linked` : 'from its T&M Rates') : 'from its T&M Rates'}</span>
            {src === key && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#fff', background: key === 'rate_sheet' ? '#0f766e' : '#6d28d9', borderRadius: 999, padding: '2px 8px', textTransform: 'none', letterSpacing: 0 }}>first on this card</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {opts.map(o => (
              <span key={o.value} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '6px 10px', borderRadius: 8, background: '#fff', border: '1px solid #e5e7eb', fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: '#111' }}>{o.label}</span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>{o.sub}</span>
              </span>
            ))}
          </div>
          {data.days.length === 0 && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: '#4b5563' }}>
              {canEdit ? <>Nothing logged yet. Press <b>+ Add Day</b> below — each of these will be on the day with a box for the quantity.</> : 'Nothing logged yet.'}
            </div>
          )}
        </div>
      ))}
      {data && onlyCommon && quotedOpts.length === 0 && (
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>No common items on this job yet — they come from the job’s T&amp;M Rates.{canEdit ? ' Add one here and it shows on every day with a box for the quantity.' : ''}</div>
      )}
      {/* …and before the first day there is nowhere else to add one from. */}
      {data && !onlyStandard && (data.days.length === 0 || (onlyCommon && quotedOpts.length === 0)) && canEdit && projectId && (addingCommonTop
        ? <AddCommonItem projectId={projectId} workflow={workflow} have={quotedOpts.map(o => String(o.label || '').toLowerCase())} onClose={() => setAddingCommonTop(false)} />
        : <button onClick={() => setAddingCommonTop(true)}
            style={{ marginBottom: 14, padding: '7px 12px', borderRadius: 8, border: '1px dashed #c4b5fd', background: '#faf5ff', color: '#6d28d9', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
            ＋ Add a common item to this job
          </button>)}

      {/* Summary by day (office only). */}
      {canSeeMoney && data && openDays.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {openDays.map(d => (
            <div key={d.id} style={{ border: '1px solid #eef0f2', borderRadius: 9, padding: '7px 12px', background: '#f8fafc' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>{shortDate(d.work_date)}</div>
              {/* A day with nothing on it bills nothing — say so, rather than a bare $0. */}
              {(d.lines || []).length === 0
                ? <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309' }}>no items yet</div>
                : <div style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{money(viewTotal(d))}</div>}
            </div>
          ))}
        </div>
      )}

      {!data ? <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Billing in parts (Pat, Oct 9 2026 — WO26117): close what was billed as Part A; new
              work goes on Part B and the next bill only has B. */}
          {canManageParts && (bill.parts.length > 0 || bill.invoices.length > 0 || bill.suggest.bill_ref) && (bill.open.day_count > 0 || bill.parts.some(p => p.status === 'closed')) && (
            <div style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5 }}>Billing parts</span>
                {bill.parts.filter(p => p.status === 'closed').map(p => (
                  <span key={p.id} style={{ fontSize: 12, fontWeight: 700, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 999, padding: '3px 10px' }}>🔒 {p.label}{p.invoice_no ? ` · #${p.invoice_no}` : p.bill_ref ? ` · ${p.bill_ref}` : ''}</span>
                ))}
                <span style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', background: '#fff', border: '1px solid #93c5fd', borderRadius: 999, padding: '3px 10px' }}>{bill.open.label} · open · {bill.open.day_count} day{bill.open.day_count === 1 ? '' : 's'}</span>
                {bill.open.day_count > 0 && !closing && (
                  <button onClick={() => setClosing({ name: bill.suggest.name || bill.open.name, through: bill.suggest.through || ymd(new Date()), invoice_id: bill.suggest.invoice_id || '' })}
                    style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 8, border: '1px solid #1e40af', background: '#1e40af', color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                    ✂ Close {bill.open.label} as billed…
                  </button>
                )}
              </div>
              {closing && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Name it<br />
                      <input value={closing.name} onChange={e => setClosing(c => ({ ...c, name: e.target.value }))} placeholder="A" style={{ ...cell, width: 130, fontSize: 14 }} /></label>
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Days through<br />
                      <input type="date" value={closing.through || ''} onChange={e => setClosing(c => ({ ...c, through: e.target.value }))} style={{ ...cell, fontSize: 14 }} /></label>
                    {bill.invoices.length === 0 && bill.suggest.bill_ref ? <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Billed on<br /><span style={{ display: 'inline-block', padding: '9px 0', fontSize: 14 }}>{bill.suggest.bill_ref}</span></div> :
                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Billed on<br />
                      <select value={closing.invoice_id || ''} onChange={e => setClosing(c => ({ ...c, invoice_id: e.target.value }))} style={{ ...cell, fontSize: 14 }}>
                        <option value="">— no invoice yet —</option>
                        {bill.invoices.map(i => <option key={i.id} value={i.id}>{i.invoice_no ? `Invoice ${i.invoice_no}` : 'Draft invoice'} · {i.status} · {money(i.total)}</option>)}
                      </select></label>}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#374151' }}>
                    Days {closing.through ? <>through <b>{shortDate(closing.through)}</b></> : 'on the card now'} go on <b>{partLabel(closing.name || bill.open.name)}</b> — locked, still here to read.
                    Anything after goes on the next part, and the next bill only has that.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={closePart} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1e40af', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Close {partLabel(closing.name || bill.open.name)}</button>
                    <button onClick={() => setClosing(null)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* The add-day controls sit ABOVE the days (Pat, Sep 2026): the newest day is at
              the top, so the next one is added from the top too. */}
          {canEdit && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', alignSelf: narrow ? 'stretch' : 'flex-start' }}>
              <button onClick={() => addDay()} style={{ flex: narrow ? '1 1 100%' : '0 0 auto', padding: '12px 18px', borderRadius: 10, border: '1.5px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                + Add Day
              </button>
              {/* A pole hold or a standby crew is the same thing every day — one tap adds the
                  next day already carrying the last day's items. */}
              {lastFilled && (
                <button onClick={() => addDay(undefined, lastFilled.id)} title={`Adds the next day with the same items as ${shortDate(lastFilled.work_date)}`}
                  style={{ flex: narrow ? '1 1 100%' : '0 0 auto', padding: '12px 18px', borderRadius: 10, border: '1.5px solid #15803d', background: '#f0fdf4', color: '#15803d', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                  + Add Day · same as {shortDate(lastFilled.work_date)}
                </button>
              )}
              {/* Add a day in the past — for one someone forgot to log. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>or pick a date:</span>
                <input type="date" value={pastDate} max={ymd(new Date())} onChange={e => setPastDate(e.target.value)}
                  style={{ ...cell, fontSize: 14 }} />
                <button onClick={() => pastDate && addDay(pastDate)} disabled={!pastDate}
                  style={{ padding: '9px 14px', borderRadius: 8, border: '1.5px solid #1e3a5f', background: pastDate ? '#1e3a5f' : '#e5e7eb', color: pastDate ? '#fff' : '#9ca3af', fontSize: 13, fontWeight: 800, cursor: pastDate ? 'pointer' : 'default' }}>
                  Add
                </button>
              </div>
            </div>
          )}
          {closedGroups.length > 0 && openDays.length === 0 && <div style={{ fontSize: 13, color: '#6b7280' }}>Nothing on {openLabel} yet.</div>}
          {openDays.map((day, i) => (
            <DayBlock key={day.id} day={day} base={base} canEdit={canEdit} canSeeMoney={canSeeMoney} narrow={narrow} show={show}
              defaultOpen={i === 0} projectId={projectId} workflow={workflow} quotedOpts={quotedOpts} sheetOpts={sheetOpts} ownOpts={ownOpts} sheetFrom={sheetFrom} ownLabel={ownLabel} rateSource={src} crewOpts={crewOpts} partOpts={partOpts} equipOpts={equipOpts} poOpts={poOpts} onChanged={changed}
              copySource={lastFilled && lastFilled.id !== day.id ? lastFilled : ([...(data?.days || [])].find(x => x.id !== day.id && (x.lines || []).some(l => l.category !== 'po')) || null)} />
          ))}
          {closedGroups.map(g => (
            <ClosedPart key={g.id} name={g.name} info={partInfo(g.id)} days={g.days} canSeeMoney={canSeeMoney} total={g.days.reduce((s, d) => s + (viewTotal(d) || 0), 0)}
              canManage={canManageParts} isLast={g.id === lastClosedId} onRename={renamePart} onReopen={reopenPart}
              renderDay={day => (
                <DayBlock key={day.id} day={day} base={base} canEdit={false} canSeeMoney={canSeeMoney} narrow={narrow} show={show}
                  defaultOpen={false} projectId={projectId} workflow={workflow} quotedOpts={quotedOpts} sheetOpts={sheetOpts} ownOpts={ownOpts} sheetFrom={sheetFrom} ownLabel={ownLabel} rateSource={src} crewOpts={crewOpts} partOpts={partOpts} equipOpts={equipOpts} poOpts={poOpts} onChanged={changed} copySource={null} />
              )} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── One day: notes, the four category sections, and photos ──────────────────────
function DayBlock({ day, base, projectId = null, workflow = null, canEdit, canSeeMoney, narrow, defaultOpen = true, quotedOpts = [], sheetOpts = null, ownOpts = null, sheetFrom = null, ownLabel = 'Common Items', rateSource = null, crewOpts, partOpts, equipOpts, poOpts = [], onChanged, copySource = null, show = 'all' }) {
  // Two groups on a day (Pat, Sep 2026). COMMON ITEMS — the things billed as one line at
  // a quoted rate (a pole hold day, loops by the foot, a drill day, mobilization): the
  // job's T&M Rates, listed with a quantity box each. STANDARD T&M — labor, material and
  // equipment picked from the real lists, plus POs and anything else. They used to share
  // one row of tabs with "Quoted" first; now a pole hold day and two hours of labor sit
  // on the same day in their own sections, and bill on the same invoice.
  const [tab, setTab] = useState('labor');
  const [addingCommon, setAddingCommon] = useState(false);
  const cats = CATS.filter(c => c.key !== 'quoted');
  const [editingDate, setEditingDate] = useState(false);   // office: fix a mislabelled date
  const [collapsed, setCollapsed] = useState(!defaultOpen); // days fold up; newest opens by default
  const dbase = `${base}/days/${day.id}`;
  const byCat = c => (day.lines || []).filter(l => l.category === c);
  // A card can show one half of the day (see TmProjectTracker's `show`): count and total
  // only what is on screen, and don't call a day "empty" because its other half is.
  const onlyCommon = show === 'common', onlyStandard = show === 'standard';
  const seen = (day.lines || []).filter(l => (onlyCommon ? l.category === 'quoted' : onlyStandard ? l.category !== 'quoted' : true));
  const lineCount = seen.length;
  const dayEmpty = (day.lines || []).length === 0;
  const seenTotal = show === 'all' ? day.day_total : seen.reduce((s, l) => s + (Number(l.price) || 0), 0);
  const photoCount = (day.photos || []).length;

  const saveNotes = notes => apiFetch(dbase, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ notes }) }).catch(() => {});
  const saveDate = async wd => { setEditingDate(false); if (!wd || wd === String(day.work_date).slice(0, 10)) return; await apiFetch(dbase, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ work_date: wd }) }).catch(() => {}); onChanged(); };
  const delDay = async () => { if (!window.confirm(`Remove ${shortDate(day.work_date)}?`)) return; await apiFetch(dbase, { method: 'DELETE' }).catch(() => {}); onChanged(); };
  // A line that fails to save says so — a silent miss is a day that bills $0 (WO26116, Sep 2026).
  const addLine = async body => {
    try { await apiFetch(`${dbase}/lines`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
    catch (e) { window.alert(e?.error || e?.message || 'That item did not save — check your connection and add it again.'); }
    onChanged();
  };
  // An empty day can take the same items as another day in one tap.
  const copyFrom = async () => {
    if (!copySource) return;
    try { await apiFetch(`${dbase}/copy-from`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from_day_id: copySource.id }) }); }
    catch (e) { window.alert(e?.error || e?.message || 'Could not copy that day.'); }
    onChanged();
  };
  const patchLine = async (id, body) => {
    try { await apiFetch(`${dbase}/lines/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); }
    catch (e) { window.alert(e?.error || e?.message || 'That change did not save — check your connection and try again.'); }
    onChanged();
  };
  const delLine = async id => {
    try { await apiFetch(`${dbase}/lines/${id}`, { method: 'DELETE' }); }
    catch (e) { window.alert(e?.error || e?.message || 'That item was not removed — check your connection and try again.'); }
    onChanged();
  };
  const uploadPhotos = async files => {
    const fd = new FormData(); files.forEach(f => fd.append('photos', f));
    await fetch(`${API_BASE}${dbase}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd }).catch(() => {});
    onChanged();
  };
  const delPhoto = async id => { await apiFetch(`${dbase}/photos/${id}`, { method: 'DELETE' }).catch(() => {}); onChanged(); };

  return (
    <div style={{ border: '1px solid #e8eaed', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: '#f8fafc', borderBottom: collapsed ? 'none' : '1px solid #eef0f2' }}>
        <button onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand this day' : 'Collapse this day'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0, width: 16, flexShrink: 0 }}>
          {collapsed ? '▸' : '▾'}
        </button>
        {canEdit && editingDate ? (
          <input type="date" defaultValue={String(day.work_date).slice(0, 10)} max={ymd(new Date())} autoFocus
            onBlur={e => saveDate(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveDate(e.target.value); if (e.key === 'Escape') setEditingDate(false); }}
            style={{ ...cell, fontSize: 15, fontWeight: 700 }} />
        ) : (
          <div onClick={canEdit ? () => setEditingDate(true) : undefined}
            title={canEdit ? 'Click to fix the date' : undefined}
            style={{ fontSize: 15, fontWeight: 800, color: '#111', cursor: canEdit ? 'pointer' : 'default' }}>
            {shortDate(day.work_date)}{canEdit && <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 5 }}>✎</span>}
          </div>
        )}
        {collapsed && lineCount > 0 && <div style={{ fontSize: 12, color: '#9ca3af' }}>{`${lineCount} item${lineCount !== 1 ? 's' : ''}`}{photoCount ? ` · ${photoCount}📷` : ''}</div>}
        {/* Nothing on the day = nothing billed. Say it, and offer the one-tap fill. */}
        {dayEmpty && <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px' }}>no items yet — bills $0</div>}
        {!dayEmpty && lineCount === 0 && <div style={{ fontSize: 12, color: '#9ca3af' }}>{onlyCommon ? 'no quantities this day' : 'no labor, material or equipment this day'}</div>}
        {dayEmpty && canEdit && copySource && (
          <button onClick={copyFrom} title={`Put the same items on this day as ${shortDate(copySource.work_date)}`}
            style={{ padding: '4px 10px', borderRadius: 7, border: '1.5px solid #15803d', background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Same as {shortDate(copySource.work_date)}
          </button>
        )}
        {canSeeMoney && <div style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 800, color: '#111' }}>{money(seenTotal)}</div>}
        {canEdit && <button onClick={delDay} title="Remove day" style={{ marginLeft: canSeeMoney ? 8 : 'auto', background: 'none', border: 'none', color: '#c9ccd1', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>}
      </div>

      {!collapsed && (() => {
        // Three groups on a day (Pat, Sep 2026): RATE SHEET — the contract's lines, linked;
        // COMMON ITEMS — this job's own quoted rates (a pole hold day, loops by the foot);
        // STANDARD T&M — labor, material and equipment from the real lists, POs, anything
        // else. Whichever the job's T&M Rates tile says leads comes first; notes and photos
        // stay at the bottom.
        const sheet = sheetOpts || quotedOpts.filter(o => o.sheet);
        const own = ownOpts || quotedOpts.filter(o => !o.sheet);
        const quotedLines = byCat('quoted');
        const allKnown = new Set(quotedOpts.map(o => String(o.value)));
        const orphanHome = (rateSource === 'rate_sheet' && sheet.length) || !own.length ? 'sheet' : 'common';
        const rows = (opts, home) => <QuotedRows lines={quotedLines} quotedOpts={opts} knownIds={allKnown} showOrphans={orphanHome === home} canEdit={canEdit} canSeeMoney={canSeeMoney} narrow={narrow} onAdd={addLine} onPatch={patchLine} onDelete={delLine} />;
        const sheetBlock = !onlyStandard && sheet.length > 0 ? (
          <div key="sheet" style={{ marginBottom: 12 }}>
            <div style={{ ...secHead, color: '#0f766e' }}>Job Rate Sheet <span style={secSub}>{sheetFrom ? `· from ${sheetFrom} · linked` : '· this job’s rate lines'}</span></div>
            {rows(sheet, 'sheet')}
          </div>
        ) : null;
        const commonBlock = !onlyStandard && (own.length > 0 || (sheet.length === 0 && quotedLines.length > 0)) ? (
          <div key="common" style={{ marginBottom: 12 }}>
            <div style={secHead}>{ownLabel} <span style={secSub}>· this job’s quoted rates</span></div>
            {rows(own, 'common')}
          </div>
        ) : null;
        const addCommon = !onlyStandard && canEdit && projectId ? (addingCommon
          ? <AddCommonItem key="addc" projectId={projectId} workflow={workflow} have={own.map(o => String(o.label || '').toLowerCase())} onClose={() => setAddingCommon(false)} />
          : <button key="addc" onClick={() => setAddingCommon(true)}
              style={{ marginBottom: 14, padding: '7px 12px', borderRadius: 8, border: '1px dashed #c4b5fd', background: '#faf5ff', color: '#6d28d9', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
              ＋ Add a common item to this job
            </button>) : null;
        const standardBlock = !onlyCommon ? (
          <div key="standard" style={{ marginBottom: 12 }}>
            {show === 'all' && <div style={secHead}>Standard T&amp;M <span style={secSub}>· labor, material, equipment</span></div>}
            {/* Category tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {cats.map(c => {
                const n = byCat(c.key).length;
                const on = tab === c.key;
                return (
                  <button key={c.key} onClick={() => setTab(c.key)}
                    style={{ flex: narrow ? '1 1 40%' : '0 0 auto', padding: '8px 12px', borderRadius: 20, border: `1.5px solid ${on ? '#1e3a5f' : '#e5e7eb'}`, background: on ? '#1e3a5f' : '#fff', color: on ? '#fff' : '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    {c.icon} {c.label}{n ? ` (${n})` : ''}
                  </button>
                );
              })}
            </div>
            <CatSection cat={tab} lines={byCat(tab)} canEdit={canEdit} canSeeMoney={canSeeMoney} narrow={narrow}
              quotedOpts={quotedOpts} crewOpts={crewOpts} partOpts={partOpts} equipOpts={equipOpts} poOpts={poOpts}
              onAdd={addLine} onPatch={patchLine} onDelete={delLine} />
          </div>
        ) : null;
        // Whichever structure the job was set up with leads; Standard T&M is labor / material / equipment.
        const order = rateSource === 'standard' ? [standardBlock, commonBlock, addCommon, sheetBlock]
          : rateSource === 'rate_sheet' ? [sheetBlock, commonBlock, addCommon, standardBlock]
          : [commonBlock, addCommon, sheetBlock, standardBlock];
        return (
          <div style={{ padding: 12 }}>
            {order}
            {!onlyCommon && (
              <div style={{ marginTop: 12, borderTop: '1px dashed #eef0f2', paddingTop: 10 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Notes</div>
                {canEdit
                  ? <textarea defaultValue={day.notes || ''} onBlur={e => saveNotes(e.target.value)} rows={2} placeholder="What happened this day…"
                      style={{ ...cell, width: '100%', resize: 'vertical' }} />
                  : <div style={{ fontSize: 14, color: day.notes ? '#374151' : '#9ca3af' }}>{day.notes || 'No notes.'}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0 0', alignItems: 'center' }}>
                  {(day.photos || []).map(p => (
                    <AttachThumb key={p.id} size={64} src={photoUrl(p.filename)} name={p.original_name || p.filename} href={photoUrl(p.filename)} onRemove={canEdit ? () => delPhoto(p.id) : undefined} />
                  ))}
                  {canEdit && <AttachButton multiple icon="📷" label="Photo" style={{ padding: '9px 12px', fontSize: 13 }} onFiles={uploadPhotos} />}
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ── One category's lines + an add row ───────────────────────────────────────────
const secHead = { fontSize: 11.5, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 7px' };
const secSub = { fontWeight: 600, color: '#9ca3af', textTransform: 'none', letterSpacing: 0 };

// Put a common item on this job, from the card. Anyone who can log a day can — the field
// too: the list carries names and units (the rate only for people who see money), the
// pick sends just the item's key, and the server applies the Price Book rate. It becomes a
// line on the job's T&M Rates, so it shows on every day with a quantity box. Items marked
// for THIS card's workflow come first, then "Any job"; the rest only when searched for.
// An item with no rate in the book is listed but can't be added — a rate-less line would
// bill $0 without anyone noticing.
const GROUP_LABEL = { card: 'For this card', any: 'Any job', other: 'Everything else' };
export function AddCommonItem({ projectId, workflow = null, have = [], onClose }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [added, setAdded] = useState([]);
  useEffect(() => {
    apiFetch(`/common-items${workflow ? `?workflow=${workflow}` : ''}`).then(r => setList(Array.isArray(r) ? r : [])).catch(() => setList([]));
  }, [workflow]);
  const ql = q.trim().toLowerCase();
  const shown = useMemo(() => (list || []).filter(o => ql ? o.name.toLowerCase().includes(ql) : o.group !== 'other'), [list, ql]);
  const hiddenOthers = !ql && (list || []).some(o => o.group === 'other');
  async function add(o) {
    setBusy(o.key); setErr('');
    try {
      await apiFetch(`/projects/${projectId}/tm-items/from-common`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key: o.key }) });
      setAdded(a => [...a, o.name.toLowerCase()]);
      window.dispatchEvent(new CustomEvent('titan:tm-items-changed'));
    } catch (e) { setErr(e?.error || e?.message || 'That item did not go on the job — try again.'); }
    setBusy('');
  }
  const onJob = name => have.includes(name.toLowerCase()) || added.includes(name.toLowerCase());
  return (
    <div style={{ border: '1.5px solid #ddd6fe', background: '#faf5ff', borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#6d28d9', flex: 1 }}>Add a common item to this job</div>
        <button onClick={onClose} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, color: '#6b7280', cursor: 'pointer' }}>Done</button>
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search — pole hold, loops, a drill, mobilization…" style={{ ...cell, width: '100%', marginBottom: 8 }} />
      {list === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div> : (
        <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {['card', 'any', 'other'].map(g => {
            const rows = shown.filter(o => o.group === g);
            if (!rows.length) return null;
            return (
              <div key={g}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{GROUP_LABEL[g]}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 6 }}>
                  {rows.map(o => {
                    const there = onJob(o.name), noRate = !o.has_rate;
                    return (
                      <button key={o.key} onClick={() => add(o)} disabled={there || noRate || busy === o.key}
                        title={there ? 'Already on this job' : noRate ? 'No rate in the Price Book yet — the office sets it in Settings → Price Book' : 'Add to this job'}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', padding: '9px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: there || noRate ? 'default' : 'pointer', opacity: there || noRate ? 0.55 : 1 }}>
                        <span style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</span>
                        <span style={{ fontWeight: 800, whiteSpace: 'nowrap', color: noRate ? '#b45309' : '#111' }}>
                          {there ? 'on this job' : noRate ? 'no rate yet' : (o.rate != null ? `${money(o.rate)}/${o.unit}` : `per ${o.unit}`)}
                        </span>
                        {!there && !noRate && <span style={{ color: '#6d28d9', fontWeight: 800 }}>{busy === o.key ? '…' : '+'}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {shown.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>{ql ? 'Nothing matches that.' : 'No common items are set up for this kind of card yet.'}</div>}
          {hiddenOthers && <div style={{ fontSize: 11.5, color: '#9ca3af' }}>Items marked for other kinds of work show when you search for them.</div>}
        </div>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: '#b91c1c' }}>{err}</div>}
      <div style={{ marginTop: 8, fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 }}>It goes on this job’s T&amp;M Rates at the Price Book rate, so it shows on every day with a box to fill.</div>
    </div>
  );
}

// The job's quoted items, already on the day (Pat, Sep 2026: "prepopulate with those
// items on the work flow card"). Every item on the job's T&M Rates is listed with an
// empty quantity box: type what happened and it is logged, change it and it updates,
// clear it and it comes off. A box left blank is not a line at all — nothing is saved
// for it, so it can't print a $0.00 row on the invoice. This replaced "pick a quoted
// item from a list, type a quantity, press Add" — three steps per item, and the items
// were the same ones every day.
function QuotedRows({ lines, quotedOpts, knownIds = null, showOrphans = true, canEdit, canSeeMoney, narrow, onAdd, onPatch, onDelete }) {
  const known = knownIds || new Set(quotedOpts.map(o => String(o.value)));
  const orphans = showOrphans ? lines.filter(l => !known.has(String(l.ref_id))) : [];   // logged against an item since taken off the quote
  const commit = (o, first, raw) => {
    const v = String(raw).trim();
    const n = v === '' ? 0 : Number(v);
    if (!Number.isFinite(n) || n < 0) return false;
    if (n === 0) { if (first) onDelete(first.id); return true; }
    if (first) { if (n !== Number(first.quantity)) onPatch(first.id, { quantity: n }); return true; }
    onAdd({ category: 'quoted', ref_type: 'tm_item', ref_id: o.value, description: o.label, quantity: n, unit: o.unit });
    return true;
  };
  const rowS = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fafbfc', borderRadius: 8, flexWrap: narrow ? 'wrap' : 'nowrap' };
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {quotedOpts.map(o => {
          const mine = lines.filter(l => String(l.ref_id) === String(o.value));
          const first = mine[0] || null;
          const price = mine.reduce((t, l) => t + (Number(l.price) || 0), 0);
          const isHours = /^h(ou)?rs?$/i.test(o.unit || '');
          return (
            <React.Fragment key={o.value}>
              <div style={{ ...rowS, background: first ? '#f0fdf4' : '#fafbfc', border: `1px solid ${first ? '#bbf7d0' : 'transparent'}` }}>
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{o.sub}</div>
                </div>
                {canEdit ? (
                  <>
                    {!first && !isHours && (
                      <button onClick={() => commit(o, null, '1')} title={`One ${o.unit || ''} today`}
                        style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#1e3a5f', fontSize: 13, fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>+1</button>
                    )}
                    <input type="number" step="any" min="0" inputMode="decimal" aria-label={`${o.label} — quantity today`}
                      key={`${o.value}-${first ? first.id : 'new'}-${first ? first.quantity : ''}`}
                      defaultValue={first && first.quantity != null ? first.quantity : ''} placeholder="0"
                      onBlur={e => { if (!commit(o, first, e.target.value)) e.target.value = first && first.quantity != null ? first.quantity : ''; }}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      style={{ ...cell, width: 84, textAlign: 'right', fontWeight: 700, flexShrink: 0 }} />
                  </>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 700, color: first ? '#111' : '#c9ccd1', flexShrink: 0 }}>{first && first.quantity != null ? first.quantity : '—'}</span>
                )}
                <span style={{ fontSize: 13, color: '#6b7280', width: 38, flexShrink: 0 }}>{o.unit || ''}</span>
                {canSeeMoney && <span style={{ fontSize: 14, fontWeight: 700, color: first ? '#111' : '#c9ccd1', textAlign: 'right', minWidth: 78, flexShrink: 0 }}>{first ? money(price) : '—'}</span>}
              </div>
              {/* The same item logged twice on one day (from before the boxes) — shown so it can be taken off. */}
              {mine.slice(1).map(l => (
                <div key={l.id} style={{ ...rowS, marginLeft: 16, fontSize: 13 }}>
                  <span style={{ flex: 1, color: '#6b7280' }}>also logged: {l.quantity}{l.unit ? ' ' + l.unit : ''}</span>
                  {canSeeMoney && <span style={{ fontWeight: 700 }}>{money(l.price)}</span>}
                  {canEdit && <button onClick={() => onDelete(l.id)} aria-label="Remove this extra line" style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>}
                </div>
              ))}
            </React.Fragment>
          );
        })}
        {orphans.map(l => (
          <div key={l.id} style={{ ...rowS, background: '#fffbeb' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{l.description || '—'} <span style={{ fontSize: 11, fontWeight: 800, color: '#b45309' }}>· no longer on the quote</span></span>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{l.quantity != null ? `${l.quantity}${l.unit ? ' ' + l.unit : ''}` : ''}</span>
            {canSeeMoney && <span style={{ fontWeight: 700 }}>{l.price ? money(l.price) : money(0)}</span>}
            {canEdit && <button onClick={() => onDelete(l.id)} aria-label="Remove this line" style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
        {canEdit ? 'Type a quantity on the ones that happened today — anything left blank bills nothing. ' : ''}Anything that isn’t one of these goes under Standard T&amp;M below.
      </div>
    </div>
  );
}

function CatSection({ cat, lines, canEdit, canSeeMoney, narrow, quotedOpts = [], crewOpts, partOpts, equipOpts, poOpts = [], onAdd, onPatch, onDelete }) {
  const meta = CAT[cat];
  const isPicker = cat !== 'other';
  const isQuoted = cat === 'quoted';
  const [pick, setPick] = useState('');
  const [desc, setDesc] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState(meta.unit);
  const [custom, setCustom] = useState(false);   // typing an item that isn't in the list
  useEffect(() => { setUnit(meta.unit); setPick(''); setDesc(''); setQty(''); setCustom(false); }, [cat]); // eslint-disable-line

  const opts = isQuoted ? quotedOpts : cat === 'labor' ? crewOpts : cat === 'material' ? partOpts : cat === 'equipment' ? equipOpts : cat === 'po' ? poOpts : [];
  // A PO is one thing bought once — quantity 1 the moment it's picked.
  useEffect(() => { if (cat === 'po' && pick && qty === '') setQty('1'); }, [cat, pick]); // eslint-disable-line
  const typing = !isPicker || custom;   // free-text entry
  // A quoted line's unit is the item's own (day, hr, trip, ft…); a mobilization is one
  // trip, so its quantity is filled in as soon as it's picked.
  const picked = isQuoted && pick ? quotedOpts.find(o => String(o.value) === String(pick)) : null;
  useEffect(() => {
    if (!picked) return;
    setUnit(picked.unit || '');
    // A day rate, a trip, a mobilization: one of them is the usual answer, so the box
    // starts at 1. Hours are the exception — those get typed.
    if (qty === '' && (picked.type === 'mobilization' || !/^h(ou)?rs?$/i.test(picked.unit || ''))) setQty('1');
  }, [picked]); // eslint-disable-line
  // Something picked or typed but not yet added — the Add button says so, because a
  // picked item that never gets added is a day that bills nothing.
  const pending = typing ? !!desc.trim() : !!pick;

  function add() {
    const q = qty === '' ? null : Number(qty);
    if (typing) {
      if (!desc.trim()) return;
      // Custom line: no rate to derive from — the office prices it later.
      onAdd({ category: cat, description: desc.trim(), quantity: q, unit });
    } else {
      if (!pick) return;
      const o = opts.find(x => String(x.value) === String(pick));
      onAdd({ category: cat, ref_type: meta.src === 'crew' ? 'crew' : meta.src, ref_id: pick, description: o ? o.label : '', quantity: q, unit: isQuoted ? (o?.unit || unit) : unit });
    }
    setPick(''); setDesc(''); setQty('');
  }

  if (isQuoted) return <QuotedRows lines={lines} quotedOpts={quotedOpts} canEdit={canEdit} canSeeMoney={canSeeMoney} narrow={narrow} onAdd={onAdd} onPatch={onPatch} onDelete={onDelete} />;

  return (
    <div>
      {lines.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          {lines.map(l => {
            const custom = !l.ref_id;   // typed in, not picked → office prices it
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, padding: '8px 10px', background: '#fafbfc', borderRadius: 8 }}>
                <span style={{ flex: 1, minWidth: 0, color: '#111', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.description || '—'}
                  {custom && cat !== 'other' && <span style={{ fontSize: 10, fontWeight: 800, color: '#b45309', background: '#fffbeb', borderRadius: 4, padding: '1px 5px', marginLeft: 6 }}>custom</span>}
                </span>
                <span style={{ color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{l.quantity != null ? `${l.quantity}${l.unit ? ' ' + l.unit : ''}` : ''}</span>
                {canSeeMoney && (
                  cat === 'po' && !custom && canEdit
                    // A PO line bills at what the PO cost — or at whatever is typed here (Ryan,
                    // Oct 21 2026: a PO written with no amount showed $0.00 and couldn't be fixed
                    // from the day). Blank goes back to the PO's own cost.
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }} title={l.unit_price != null ? 'Billed at the price typed here — clear it to go back to the PO cost' : 'Billed at the PO cost — type a price to change it'}>
                        $<input key={l.id + ':' + (l.unit_price ?? '')} type="number" step="any" defaultValue={l.unit_price ?? ''} placeholder={l.price ? String(Math.round(l.price / (Number(l.quantity) || 1) * 100) / 100) : '0.00'}
                          onBlur={e => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== (l.unit_price ?? null)) onPatch(l.id, { unit_price: v }); }}
                          style={{ ...cell, width: 86, padding: '5px 6px', textAlign: 'right', borderColor: !l.price && l.unit_price == null ? '#fca5a5' : undefined }} /></span>
                  : custom && canEdit
                    // Office prices a custom item right here — $ each.
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }} title="Price each — office">
                        $<input type="number" step="any" defaultValue={l.unit_price ?? ''} placeholder="rate"
                          onBlur={e => onPatch(l.id, { unit_price: e.target.value === '' ? null : Number(e.target.value) })}
                          style={{ ...cell, width: 64, padding: '5px 6px', textAlign: 'right' }} /></span>
                    : <span style={{ color: l.price ? '#111' : '#b45309', fontWeight: 700, textAlign: 'right', minWidth: 60, flexShrink: 0 }}>{l.price ? money(l.price) : (custom ? 'to price' : (cat === 'other' ? '—' : money(0)))}</span>
                )}
                {canEdit ? <button onClick={() => onDelete(l.id)} style={{ background: 'none', border: 'none', color: '#c9ccd1', fontSize: 16, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}>×</button> : null}
              </div>
            );
          })}
        </div>
      )}
      {canEdit && (<>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: narrow ? '1 1 100%' : '1 1 170px', minWidth: narrow ? 0 : 170 }}>
            {typing
              ? <input value={desc} onChange={e => setDesc(e.target.value)} autoFocus={custom} placeholder={isPicker ? `Type the ${meta.label.toLowerCase()}…` : 'Describe it'} style={{ ...cell, width: '100%' }} />
              : <SearchSelect value={pick} options={opts} onChange={setPick}
                  onCreate={isQuoted ? undefined : (text => { setCustom(true); setPick(''); setDesc(text); })}
                  placeholder={isQuoted ? 'Pick a quoted item…' : `Pick ${meta.label.toLowerCase()}…`} inputStyle={{ ...cell, width: '100%' }} />}
          </div>
          <input type="number" step="any" value={qty} onChange={e => setQty(e.target.value)} placeholder={(isQuoted ? (picked?.unit || 'qty') : meta.unit) || 'qty'} style={{ ...cell, width: 80 }} />
          {isQuoted && picked?.unit && <span style={{ fontSize: 13, color: '#6b7280' }}>{picked.unit}{Number(qty) === 1 || picked.unit === 'hr' ? '' : 's'}</span>}
          {cat === 'equipment' && (
            <select value={unit} onChange={e => setUnit(e.target.value)} style={{ ...cell, width: 78 }}>
              <option value="day">/day</option><option value="hr">/hr</option>
            </select>
          )}
          <button onClick={add} style={{ flex: narrow ? '1 1 100%' : '0 0 auto', padding: '9px 18px', borderRadius: 8, border: 'none', background: pending ? '#e87722' : '#1e3a5f', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: pending ? '0 0 0 3px rgba(232,119,34,0.25)' : 'none' }}>{pending ? 'Add — not saved yet' : 'Add'}</button>
        </div>
        {/* Not in the list? Type it in — the office prices it later. Quoted lines are
            the quote itself, so anything not on it belongs on the other tabs. */}
        {isQuoted && (
          <div style={{ marginTop: 6, fontSize: 12.5, color: '#6b7280' }}>These are the rates on this job's quote. Something not quoted goes under Labor, Material or Equipment.</div>
        )}
        {cat === 'po' && (
          <div style={{ marginTop: 6, fontSize: 12.5, color: '#6b7280' }}>
            {poOpts.length ? `The purchase orders written on this job, newest first (${poOpts.length}). A PO bills at what it cost${canSeeMoney ? ' — or type the amount in the $ box on its line (clear the box to go back to the PO cost)' : ''}.` : 'No purchase orders on this job yet. Write one from the job page and it shows up here.'}
          </div>
        )}
        {isPicker && !isQuoted && (
          <button onClick={() => { setCustom(c => !c); setPick(''); setDesc(''); }}
            style={{ marginTop: 6, background: 'none', border: 'none', color: '#6b7280', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            {custom ? '‹ Pick from the list instead' : `Not in the list? Type in a ${meta.label.toLowerCase()} item`}
          </button>
        )}
      </>)}
    </div>
  );
}
